import React from 'react';
import { Field } from 'redux-form';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import TagManager from 'react-gtm-module';
import Dialog from '@material-ui/core/Dialog';
import Toolbar from '@material-ui/core/Toolbar';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import Typography from '@material-ui/core/Typography';
import { createStructuredSelector } from 'reselect';

import { VIEWABLE_FORM_MODES } from '../../constants';
import { constructGTM } from '../../utilities/helpers';
import styles from './styles';
import JHLogoIcon from '../JHLogoIcon';
import JobForm from '../JobForm';
import { closeModal } from '../../store/actions/modal';
import {
  required,
  email,
  phone,
  australianAddress,
} from '../../utilities/validators';
import { createJob, updateJob } from '../../store/actions/jobs';
import ViewableTextField from '../../fields/reduxForm/ViewableTextField';
import SelectField from '../../fields/reduxForm/SelectField';
import PostcodeSearchField from '../../fields/reduxForm/PostcodeSearchField/index';
import { selectIsCreatingJob } from '../../store/selectors';

export const JOB_MODAL_ACTION = {
  CREATE: 'create',
  UPDATE: 'update',
  VIEW: 'view',
};

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  onCreateJob: PropTypes.func,
  onUpdateJob: PropTypes.func,
  isCreatingJob: PropTypes.bool,
  fullScreen: PropTypes.bool,
  formMode: PropTypes.oneOf([
    VIEWABLE_FORM_MODES.EDITING,
    VIEWABLE_FORM_MODES.VIEWING,
  ]),
  // What Job action the modal performs
  jobModalAction: PropTypes.oneOf([
    JOB_MODAL_ACTION.CREATE,
    JOB_MODAL_ACTION.VIEW,
    JOB_MODAL_ACTION.UPDATE,
  ]),
  jobDetails: PropTypes.shape({
    id: PropTypes.string.isRequired,
    dateCreated: PropTypes.string.isRequired,
    customerDetail: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      phoneNumber: PropTypes.string,
    }),
    notes: PropTypes.string,
    budget: PropTypes.string,
    typeOfJob: PropTypes.string,
    plannedStartDate: PropTypes.string,
    locationOfProject: PropTypes.string,
  }),
  onCloseModal: PropTypes.func,
};

const defaultProps = {};

export class JobModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      locationHasChanged: false,
      budgetOption: [],
    };
  }

  handleBudgetOption = value => {
    this.setState(() => ({ budgetOption: value }));
  };

  handleFormSubmit = async values => {
    const { jobModalAction, jobDetails, onUpdateJob, onCreateJob } = this.props;
    const accessToken = localStorage.getItem('accessToken');
    const payload = {
      customerDetail: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
      },
      notes: values.notes,
      budget: values.budget,
      typeOfJob: values.typeOfJob,
      plannedStartDate: values.plannedStartDate,
      locationOfProject: values.locationOfProject,
    };

    // Log event to GTM
    TagManager.initialize(
      constructGTM({
        dataLayer: {
          event: 'User Created New Job',
        },
      }),
    );

    if (jobModalAction === JOB_MODAL_ACTION.UPDATE) {
      onUpdateJob(accessToken, jobDetails.rawId, payload);
    } else {
      onCreateJob(accessToken, payload);
    }
  };

  typeOfJobField = props => {
    if (props.input.value === '') {
      this.handleBudgetOption(['', 'Select Type Of Job']);
    }

    if (props.input.value === 'New Builds') {
      this.handleBudgetOption([
        '',
        'Under $100k',
        '$100k – $400k',
        '$400k – $800k',
        'More than $800k',
      ]);
    }
    if (props.input.value === 'Renovations') {
      this.handleBudgetOption([
        '',
        'Under $100k',
        '$100k – $200k',
        '$200k – $300k',
        '$300k – $400k',
        'More than $400k',
      ]);
    }
    if (props.input.value === 'Other') {
      this.handleBudgetOption([
        '',
        'Under $100k',
        '$100k – $400k',
        '$400k – $800k',
        'More than $800k',
      ]);
    }
    const { id, classes } = props;
    // const options = ['', 'New builds', 'Renovations', 'Other'];
    const options = ['', 'New Builds', 'Renovations', 'Other'];

    return (
      <Field
        id={id}
        name="typeOfJob"
        component={SelectField}
        label="Type Of Job"
        validate={[required]}
        classes={classes}
      >
        {options.map(option => (
          <option value={option}>{option}</option>
        ))}
      </Field>
    );
  };

  plannedStartDateField = props => {
    const { id, classes } = props;
    const options = [
      '',
      '0-3 Months',
      '3-6 Months',
      '6-12 Months',
      '> 12 Months',
      'Unsure',
    ];
    return (
      <Field
        id={id}
        className="plannedStartDate"
        name="plannedStartDate"
        component={SelectField}
        label="Planned Start Date"
        validate={[required]}
        classes={classes}
      >
        {options.map(option => (
          <option value={option}>{option}</option>
        ))}
      </Field>
    );
  };

  handleLocationChange = () =>
    this.setState({
      locationHasChanged: true,
    });

  locationOfProjectField = props => {
    const { id, classes } = props;
    return (
      <Field
        id={id}
        name="locationOfProject"
        component={PostcodeSearchField}
        handleLocationChange={this.handleLocationChange}
        label="Suburb of Job"
        validate={[required]}
        classes={classes}
      />
    );
  };

  getModalTitle = () => {
    const { jobModalAction } = this.props;
    switch (jobModalAction) {
      case JOB_MODAL_ACTION.VIEW:
        return 'Job Details';
      case JOB_MODAL_ACTION.CREATE:
        return 'Create a Job';
      default:
        return 'Update Job';
    }
  };

  getInitialFormValues = () => {
    //  Get existing values from the db if present, otherwise default them to blank
    const { jobDetails } = this.props;
    const firstName =
      jobDetails != null && jobDetails.customerDetail != null
        ? jobDetails.customerDetail.firstName
        : '';
    const lastName =
      jobDetails != null && jobDetails.customerDetail != null
        ? jobDetails.customerDetail.lastName
        : '';
    const customerDetailEmail =
      jobDetails != null && jobDetails.customerDetail != null
        ? jobDetails.customerDetail.email
        : '';
    const phoneNumber =
      jobDetails != null && jobDetails.customerDetail != null
        ? jobDetails.customerDetail.phoneNumber
        : '';
    const notes = jobDetails != null ? jobDetails.notes : '';
    const budget = jobDetails != null ? jobDetails.budget : '';
    const typeOfJob = jobDetails != null ? jobDetails.typeOfJob : '';
    const plannedStartDate =
      jobDetails != null ? jobDetails.plannedStartDate : '';
    const locationOfProject =
      jobDetails != null ? jobDetails.locationOfProject : '';
    const initialValues = {
      firstName,
      lastName,
      email: customerDetailEmail,
      phoneNumber,
      notes,
      budget,
      typeOfJob,
      plannedStartDate,
      locationOfProject,
    };
    return initialValues;
  };

  budgetField = props => {
    const { id, classes } = props;
    // Stored in an array because keys and values are the same in all cases.
    const options = this.state.budgetOption;

    //   '',
    //   'Under $15000',
    //   '$15000-$50000',
    //   'More than $50000',
    //   'Unsure',
    // ];
    return (
      <Field
        id={id}
        name="budget"
        component={SelectField}
        label="Budget"
        validate={required}
        classes={classes}
      >
        {options.map(option => (
          <option value={option}>{option}</option>
        ))}
      </Field>
    );
  };

  getFields = formMode => {
    const { classes } = this.props;
    const baseFields = [
      {
        name: 'firstName',
        component: ViewableTextField,
        props: {
          id: 'job-form-modal-firstName-text-field',
          label: 'First Name',
          fullWidth: true,
        },
        validate: [required],
        panel: 'left',
      },
      {
        name: 'lastName',
        component: ViewableTextField,
        props: {
          id: 'job-form-modal-lastName-text-field',
          label: 'Last Name',
          fullWidth: true,
        },
        validate: [required],
        panel: 'left',
      },
      {
        name: 'email',
        component: ViewableTextField,
        props: {
          id: 'job-form-modal-email-text-field',
          label: 'Email',
          fullWidth: true,
        },
        validate: [required, email],
        panel: 'left',
      },
      {
        name: 'phoneNumber',
        component: ViewableTextField,
        props: {
          id: 'job-form-modal-phone-text-field',
          label: 'Phone',
          fullWidth: true,
        },
        validate: [phone],
        panel: 'left',
      },
      {
        name: 'notes',
        component: ViewableTextField,
        props: {
          id: 'job-form-modal-note-text-field',
          label: 'Notes',
          fullWidth: true,
          multiline: true,
          rows: 5,
        },
        validate: [],
        panel: 'right',
      },
      {
        name: 'budget',
        component:
          formMode === VIEWABLE_FORM_MODES.EDITING
            ? this.budgetField
            : ViewableTextField,
        props: {
          id: 'job-form-modal-budget-text-field',
          label: 'Budget',
          fullWidth: true,
          multiline: true,
          rows: 5,
          classes,
          // typeOfJob,
        },
        validate: [required],
        panel: 'right',
      },
      {
        name: 'typeOfJob',
        component:
          formMode === VIEWABLE_FORM_MODES.EDITING
            ? this.typeOfJobField
            : ViewableTextField,
        props: {
          id: 'job-form-modal-type-of-job-text-field',
          label: 'Type Of Job',
          fullWidth: true,
          multiline: true,
          rows: 5,
          classes,
        },
        validate: [required],
        panel: 'right',
      },
      {
        name: 'plannedStartDate',
        component:
          formMode === VIEWABLE_FORM_MODES.EDITING
            ? this.plannedStartDateField
            : ViewableTextField,
        props: {
          id: 'job-form-modal-planned-start-date-text-field',
          label: 'Planned Start Date',
          fullWidth: true,
          multiline: true,
          rows: 5,
          classes,
        },
        validate: [required],
        panel: 'right',
      },
      {
        name: 'locationOfProject',
        component:
          formMode === VIEWABLE_FORM_MODES.EDITING
            ? this.locationOfProjectField
            : ViewableTextField,
        props: {
          id: 'job-form-modal-location-of-project-text-field',
          label: 'Suburb of Job',
          fullWidth: true,
          multiline: true,
          rows: 5,
          classes,
        },
        validate: this.state.locationHasChanged
          ? [required, australianAddress]
          : [required],
        panel: 'right',
      },
    ];

    return baseFields;
  };

  getFormButtonText = () => 'Submit';

  render() {
    const {
      classes,
      onClose,
      isOpen,
      isCreatingJob,
      fullScreen,
      formMode,
      onCloseModal,
    } = this.props;

    return (
      <Dialog
        fullScreen={fullScreen}
        onClose={onClose}
        disableBackdropClick={formMode === VIEWABLE_FORM_MODES.EDITING}
        onBackdropClick={
          formMode !== VIEWABLE_FORM_MODES.EDITING ? onCloseModal : null
        }
        aria-labelledby="Job Modal"
        open={isOpen}
        maxWidth="md"
        fullWidth
        classes={{
          paper: classes.modalPaper,
        }}
      >
        <div className={classes.root}>
          {!isCreatingJob && (
            <IconButton
              aria-label="Close"
              className={classes.topCloseButton}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          )}
          <div>
            <Toolbar className={classes.toolBar}>
              <JHLogoIcon />
            </Toolbar>
            <Typography variant="h5" className={classes.formTitle}>
              {this.getModalTitle()}
            </Typography>
            <Typography variant="subtitle1" className={classes.subTitle}>
              {formMode === VIEWABLE_FORM_MODES.EDITING &&
                'Enter the customer details below'}
              {formMode === VIEWABLE_FORM_MODES.VIEWING &&
                'Customer details below'}
            </Typography>
            <JobForm
              onSubmit={this.handleFormSubmit}
              fields={this.getFields(formMode)}
              initialValues={this.getInitialFormValues()}
              isSubmitting={isCreatingJob}
              isDisabled={isCreatingJob}
              buttonText={this.getFormButtonText()}
              formMode={formMode}
              classes={{
                root: classes.plannedStartDate,
              }}
            />
          </div>
        </div>
      </Dialog>
    );
  }
}

JobModal.propTypes = propTypes;
JobModal.defaultProps = defaultProps;

const mapStateToProps = createStructuredSelector({
  isCreatingJob: selectIsCreatingJob(),
});

const mapDispatchToProps = dispatch => ({
  onCloseModal: () => dispatch(closeModal()),
  onCreateJob: (accessToken, payload) =>
    dispatch(createJob(accessToken, payload)),
  onUpdateJob: (accessToken, id, payload) =>
    dispatch(updateJob(accessToken, id, payload)),
});

const StyledJobModal = withStyles(styles)(
  withMobileDialog()(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(JobModal),
  ),
);
StyledJobModal.displayName = 'styledJobModal';

export default StyledJobModal;
